import { lazy } from "react";
import { Navigate, Outlet, Route } from "react-router-dom";
import ModuleRouteGuard from "../common/components/ModuleRouteGuard";
import MainLayout from "./layouts/main-layout";
import PrivateRoute from "./private-route";

const CRMRetailMainPage = lazy(() => import("../modules/CRMRetail"));
const CRMRetailHome = lazy(() => import("../modules/CRMRetail/Home"));
const CRMRetailContacts = lazy(() => import("../modules/CRMRetail/Contacts"));
const CRMRetailPatients = lazy(() => import("../modules/CRMRetail/Patients"));
const CRMRetailLeads = lazy(() => import("../modules/CRMRetail/Leads"));
const CRMRetailTasks = lazy(() => import("../modules/CRMRetail/Tasks"));
const CRMRetailCampaign = lazy(() => import("../modules/CRMRetail/Campaign"));
const CRMRetailCallHistory = lazy(
  () => import("../modules/CRMRetail/CallHistory"),
);
const CRMRetailLibrary = lazy(() => import("../modules/CRMRetail/Library"));
const CRMContactDetails = lazy(
  () => import("../modules/CRMRetail/Contacts/ContactDetails"),
);
const TaskDetails = lazy(
  () => import("../modules/CRMRetail/Tasks/Details/TaskDetails"),
);
const CRMCompainsDetails = lazy(
  () => import("../modules/CRMRetail/Campaign/Details/CRMCompainsDetails"),
);
const NewsletterPage = lazy(
  () => import("../modules/CRMRetail/Library/NewsLetter/CRMNewsletterCampaign"),
);
const TemplatesTabs = lazy(
  () => import("../modules/CRMRetail/Library/Templates/TemplatesTabs"),
);
const TemplateTaskDetails = lazy(
  () =>
    import("../modules/CRMRetail/Library/Templates/Task/Details/TemplateTaskDetails"),
);
const TemplateCampainsDetails = lazy(
  () =>
    import("../modules/CRMRetail/Library/Templates/Campaign/Details/TemplateCampainsDetails"),
);
const CRMTemplateCampaign = lazy(
  () =>
    import("../modules/CRMRetail/Library/Templates/Campaign/CRMTemplateCampaign"),
);
const CRMTemplateTask = lazy(
  () => import("../modules/CRMRetail/Library/Templates/Task/CRMTemplateTask"),
);
const CRMRetailSettings = lazy(() => import("../modules/CRMRetail/Settings"));
const Audits = lazy(() => import("../modules/CRMRetail/Settings/Audits"));
const ContactTaskDetails = lazy(
  () =>
    import("../modules/CRMRetail/Contacts/Tasks/Details/ContactTaskDetails"),
);
const CRMPatientDetails = lazy(
  () => import("../modules/CRMRetail/Patients/ContactDetails"),
);
const PatientTaskDetails = lazy(
  () =>
    import("../modules/CRMRetail/Patients/Tasks/Details/PatientTaskDetails"),
);

const MainRoute = () => (
  <PrivateRoute>
    <MainLayout>
      <Outlet />
    </MainLayout>
  </PrivateRoute>
);

const CrmRoutes = (
  <Route path="/crm" element={<MainRoute />}>
    <Route
      index
      element={
        <ModuleRouteGuard moduleType="admin" routePath="dashboard">
          <CRMRetailMainPage />
        </ModuleRouteGuard>
      }
    />
    <Route
      path=""
      element={
        <ModuleRouteGuard moduleType="admin" routePath="dashboard">
          <CRMRetailMainPage />
        </ModuleRouteGuard>
      }
    >
      <Route index element={<Navigate to="home" replace />} />

      <Route
        path="home"
        element={
          <ModuleRouteGuard moduleType="admin" routePath="dashboard">
            <CRMRetailHome />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="contacts"
        element={
          <ModuleRouteGuard moduleType="admin" routePath="dashboard">
            <CRMRetailContacts />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="contacts/:id"
        element={
          <ModuleRouteGuard moduleType="admin" routePath="dashboard">
            <CRMContactDetails />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="contacts/:id/task/:taskUuid"
        element={
          <ModuleRouteGuard moduleType="admin" routePath="dashboard">
            <ContactTaskDetails />
          </ModuleRouteGuard>
        }
      />

      <Route
        path="patients"
        element={
          <ModuleRouteGuard moduleType="admin" routePath="dashboard">
            <CRMRetailPatients />
          </ModuleRouteGuard>
        }
      />

      <Route
        path="patients/:id"
        element={
          <ModuleRouteGuard moduleType="admin" routePath="dashboard">
            <CRMPatientDetails />
          </ModuleRouteGuard>
        }
      />

      <Route
        path="patients/:id/task/:taskUuid"
        element={
          <ModuleRouteGuard moduleType="admin" routePath="dashboard">
            <PatientTaskDetails />
          </ModuleRouteGuard>
        }
      />

      <Route
        path="leads"
        element={
          <ModuleRouteGuard moduleType="admin" routePath="dashboard">
            <CRMRetailLeads />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="tasks"
        element={
          <ModuleRouteGuard moduleType="admin" routePath="dashboard">
            <CRMRetailTasks />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="tasks/:id"
        element={
          <ModuleRouteGuard moduleType="admin" routePath="dashboard">
            <TaskDetails />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="campaign"
        element={
          <ModuleRouteGuard moduleType="admin" routePath="dashboard">
            <CRMRetailCampaign />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="campaign/:id"
        element={
          <ModuleRouteGuard moduleType="admin" routePath="dashboard">
            <CRMCompainsDetails />
          </ModuleRouteGuard>
        }
      />

      <Route
        path="call-history"
        element={
          <ModuleRouteGuard moduleType="admin" routePath="dashboard">
            <CRMRetailCallHistory />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="library"
        element={
          <ModuleRouteGuard moduleType="admin" routePath="dashboard">
            <CRMRetailLibrary />
          </ModuleRouteGuard>
        }
      >
        <Route index element={<Navigate to="template" replace />} />

        <Route
          path="template"
          element={
            <ModuleRouteGuard moduleType="admin" routePath="dashboard">
              <TemplatesTabs />
            </ModuleRouteGuard>
          }
        >
          <Route index element={<Navigate to="campaign" replace />} />

          <Route path="campaign" element={<CRMTemplateCampaign />} />
          <Route path="task" element={<CRMTemplateTask />} />

          <Route path="task/:id" element={<TemplateTaskDetails />} />
          <Route path="campaign/:id" element={<TemplateCampainsDetails />} />
        </Route>

        <Route
          path="template/task/:id"
          element={
            <ModuleRouteGuard moduleType="admin" routePath="dashboard">
              <TemplateTaskDetails />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="template/campaign/:id"
          element={
            <ModuleRouteGuard moduleType="admin" routePath="dashboard">
              <TemplateCampainsDetails />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="newsletter"
          element={
            <ModuleRouteGuard moduleType="admin" routePath="dashboard">
              <NewsletterPage />
            </ModuleRouteGuard>
          }
        />
      </Route>
    </Route>

    <Route path="settings" element={<CRMRetailSettings />}>
      <Route index element={<Navigate to="audit-log" replace />} />
      <Route path="audit-log" element={<Audits />} />
    </Route>

    <Route
      path="details/:id"
      element={
        <ModuleRouteGuard moduleType="admin" routePath="dashboard">
          <div>
            CRM Retail Details Page - ID:{" "}
            {window.location.pathname.split("/").pop()}
          </div>
        </ModuleRouteGuard>
      }
    />
    <Route
      path="new"
      element={
        <ModuleRouteGuard moduleType="admin" routePath="dashboard">
          <div>Create New CRM Retail Entry</div>
        </ModuleRouteGuard>
      }
    />
  </Route>
);

export default CrmRoutes;
